import { z } from 'zod';

const optionalUrl = z.preprocess(
  (value) => (typeof value === 'string' && value.trim() === '' ? undefined : value),
  z.string().url().optional(),
);

const EnvSchema = z.object({
  VITE_API_BASE_URL: z
    .string({ required_error: 'VITE_API_BASE_URL is required' })
    .url('VITE_API_BASE_URL must be a URL including /api/v1'),
  VITE_APP_ORIGIN: z
    .string({ required_error: 'VITE_APP_ORIGIN is required' })
    .url('VITE_APP_ORIGIN must be the public web origin'),
  VITE_MEDIA_BASE_URL: optionalUrl,
});

export type AppEnv = {
  apiBaseUrl: string;
  appOrigin: string;
  mediaBaseUrl: string | undefined;
};

export type EnvResult = { ok: true; env: AppEnv } | { ok: false; problems: string[] };

export function parseEnv(source: Record<string, unknown>): EnvResult {
  const parsed = EnvSchema.safeParse(source);
  if (!parsed.success) {
    return { ok: false, problems: parsed.error.issues.map((issue) => issue.message) };
  }
  return {
    ok: true,
    env: {
      apiBaseUrl: parsed.data.VITE_API_BASE_URL.replace(/\/$/, ''),
      appOrigin: parsed.data.VITE_APP_ORIGIN.replace(/\/$/, ''),
      mediaBaseUrl: parsed.data.VITE_MEDIA_BASE_URL?.replace(/\/$/, ''),
    },
  };
}

export const envResult = parseEnv(import.meta.env);

/** Validated environment. Only read after startup has checked `envResult.ok`. */
export function getEnv(): AppEnv {
  if (!envResult.ok) throw new Error(`Invalid configuration: ${envResult.problems.join('; ')}`);
  return envResult.env;
}
